import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { t } from 'i18next';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { formatCurrency } from 'src/utils/format-currency';
import { useEffect, useRef } from 'react';

// Types
type Props = {
  series: {
    name: string;
    type: string;
    data: number[];
  }[];
  labels: string[];
  onLegendClick?: (seriesName: string) => void;
  height?: number;
};

// Constants
const CHART_COLORS = {
  TWO_SERIES: ['#ff9800', '#5119b7'],
  MULTIPLE_SERIES: ['#5119b7', '#ffb300', '#ffd54f', '#8E33FF']
};

const PERCENTAGE_SERIES_INDICATORS = ['CTR', 'vulnerability'];
const AD_COST_SERIES_NAMES = [
  'dashboard.global.ad_cost',
  'dashboard.brandTraffic.searchAdCost',
  'dashboard.brandTraffic.shoppingAdCost',
  'dashboard.brandTraffic.pmaxAdCost'
];

const DEFAULT_HEIGHT = 450;
const MAX_PERCENTAGE = 100;
const Y_AXIS_MARGIN_MULTIPLIER = 1.3;

// Utility functions
const isPercentageData = (name: string): boolean => {
  return PERCENTAGE_SERIES_INDICATORS.some(indicator => name.includes(indicator));
};

const limitTo100 = (value: number): number => Math.min(value, MAX_PERCENTAGE);

// Function to round up to nice intervals for Y-axis
const roundUpToNiceInterval = (value: number): number => {
  if (value <= 0) return 0.5;

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;

  let niceValue;
  if (normalized <= 1.2) niceValue = 1.2;
  else if (normalized <= 1.5) niceValue = 1.5;
  else if (normalized <= 2) niceValue = 2;
  else if (normalized <= 3) niceValue = 3;
  else if (normalized <= 5) niceValue = 5;
  else if (normalized <= 7) niceValue = 7;
  else niceValue = 10;

  return niceValue * magnitude;
};

const getAdCostSeries = (series: any[]) => {
  return series.filter(serie =>
    AD_COST_SERIES_NAMES.some(name => serie.name === t(name))
  );
};

const calculateMaxAdCost = (adCostSeries: any[]): number => {
  if (!adCostSeries.length) return 0.5;

  // Separate bar series (stacked) from line series (not stacked)
  const barSeries = adCostSeries.filter(serie => serie.type === 'bar');
  const lineSeries = adCostSeries.filter(serie => serie.type === 'line' || !serie.type);

  let maxValue = 0;

  // For bar series (stacked), calculate the maximum sum at each data point
  if (barSeries.length > 0) {
    const maxStackedSum = Math.max(...Array.from({ length: barSeries[0].data.length }, (_, i) =>
      barSeries.reduce((sum, serie) => sum + (serie.data[i] || 0), 0)
    ));
    maxValue = Math.max(maxValue, maxStackedSum);
  }

  // For line series (not stacked), find the maximum individual value
  if (lineSeries.length > 0) {
    const maxLineValue = Math.max(...lineSeries.flatMap(serie =>
      serie.data.filter((value: number) => value !== null && value !== undefined)
    ));
    maxValue = Math.max(maxValue, maxLineValue);
  }

  // If no valid data found, return default
  if (maxValue === 0) return 0.5;

  // Apply margin multiplier and round to nice interval
  const maxWithMargin = maxValue * Y_AXIS_MARGIN_MULTIPLIER;
  return roundUpToNiceInterval(maxWithMargin);
};

const getChartColors = (seriesCount: number): string[] => {
  return seriesCount === 2 ? CHART_COLORS.TWO_SERIES : CHART_COLORS.MULTIPLE_SERIES;
};

const createTooltipFormatter = (workspace: any, lang: string) => {
  return (value: number, { seriesIndex, w }: any) => {
    if (typeof value === 'undefined') return value;

    const seriesName = w.config.series[seriesIndex].name;

    if (isPercentageData(seriesName)) {
      return `${fPercent(limitTo100(value))}`;
    } else if (seriesName.includes('Blended CPC')) {
      return formatCurrency(value, workspace?.currency || "USD", lang, false, 3);
    } else {
      return formatCurrency(value, workspace?.currency || "USD", lang, true, 1);
    }
  };
};

const createYAxisConfig = (adjustedSeries: any[], workspace: any, lang: string) => {
  const isTwoSeries = adjustedSeries.length === 2;

  if (isTwoSeries) {
    return [
      {
        title: { text: t('dashboard.global.ad_cost') },
        seriesName: [
          t('dashboard.global.ad_cost'),
          t('dashboard.brandTraffic.searchAdCost'),
          t('dashboard.brandTraffic.shoppingAdCost'),
          t('dashboard.brandTraffic.pmaxAdCost')
        ],
        min: 0,
        max: calculateMaxAdCost(getAdCostSeries(adjustedSeries)),
        show: true,
        labels: {
          formatter(value: number) {
            return formatCurrency(value, workspace?.currency || "USD", lang, true, 1)
          }
        }
      },
      {
        title: { text: t('dashboard.global.blended_ctr') },
        opposite: true,
        min: 0,
        max: MAX_PERCENTAGE,
        labels: {
          formatter(value: number) {
            return `${limitTo100(value).toFixed(0)}%`;
          }
        }
      }
    ];
  }

  return [
    {
      title: { text: t('dashboard.global.blended_ctr') },
      opposite: true,
      min: 0,
      max: MAX_PERCENTAGE,
      labels: {
        formatter(value: number) {
          return `${limitTo100(value).toFixed(0)}%`;
        }
      },
    },
    {
      title: { text: t('dashboard.global.ad_cost') },
      seriesName: [
        t('dashboard.global.ad_cost'),
        t('dashboard.brandTraffic.searchAdCost'),
        t('dashboard.brandTraffic.shoppingAdCost'),
        t('dashboard.brandTraffic.pmaxAdCost')
      ],
      min: 0,
      max: calculateMaxAdCost(getAdCostSeries(adjustedSeries)),
      show: true,
      labels: {
        formatter(value: number) {
          return formatCurrency(value, workspace?.currency || "USD", lang, true, 1)
        }
      }
    },
    ...(adjustedSeries.some(serie =>
      serie.name === t('dashboard.brandTraffic.searchAdCost') ||
      serie.name === t('dashboard.brandTraffic.shoppingAdCost')
    ) ? [{
      title: {
        text: t('dashboard.brandTraffic.searchAdCost') + '/' + t('dashboard.brandTraffic.shoppingAdCost')
      },
      seriesName: [
        t('dashboard.brandTraffic.searchAdCost'),
        t('dashboard.brandTraffic.shoppingAdCost'),
        t('dashboard.brandTraffic.adCost'),
        t('dashboard.brandTraffic.pmaxAdCost')
      ],
      min: 0,
      max: calculateMaxAdCost(getAdCostSeries(adjustedSeries)),
      show: true,
      labels: {
        formatter(value: number) {
          return formatCurrency(value, workspace?.currency || "USD", lang, true, 1)
        }
      }
    }] : []),
    {
      title: { text: t('dashboard.global.blended_cpc') },
      min: 0,
      max: (() => {
        const cpcSeries = adjustedSeries.find(serie => serie.name === t('dashboard.global.blended_cpc'));
        if (!cpcSeries?.data) return 0.5;
        const maxCpc = Math.max(...cpcSeries.data) * Y_AXIS_MARGIN_MULTIPLIER;
        return roundUpToNiceInterval(maxCpc);
      })(),
      show: false,
      labels: {
        formatter(value: number) {
          return formatCurrency(value, workspace?.currency || "USD", lang, false, 2)
        }
      }
    }
  ];
};

export default function ChartMixed({ series, labels, onLegendClick, height = DEFAULT_HEIGHT }: Props) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace = useCurrentWorkspace();
  const annotations = generateAnnotations(workspace, filterStore, labels);
  const chartRef = useRef(null);
  const lang: any = localStorage.getItem('i18nextLng');

  // Adjust series data to limit percentage values to 100%
  const adjustedSeries = series.map((serie) => ({
    ...serie,
    data: serie.data.map((value) => isPercentageData(serie.name) ? limitTo100(value) : value),
  }));

  const chartOptions = useChart({
    plotOptions: {
      bar: { columnWidth: '50%', borderRadius: 0 },
    },
    chart: {
      stacked: true,
      height: height,
      events: {
        mounted: (chartContext: any) => {
          try {
            if (chartContext?.hideSeries) {
              chartContext.hideSeries(t('dashboard.global.blended_cpc'));
            }
          } catch (e) {
            console.warn("Impossible de cacher la série blended_cpc :", e);
          }
        }
      },
    },
    fill: {
      colors: getChartColors(adjustedSeries.length)
    },
    colors: getChartColors(adjustedSeries.length),
    labels: labels || [],
    markers: {
      size: 6
    },
    xaxis: {},
    yaxis: createYAxisConfig(adjustedSeries, workspace, lang),
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: createTooltipFormatter(workspace, lang),
      },
    },
    annotations: {
      xaxis: annotations
    },
  });

  return (
    <Chart
      ref={chartRef}
      dir="ltr"
      type="line"
      series={adjustedSeries}
      options={chartOptions}
      sx={{ width: { xs: '100%', lg: '95%' } }}
      height={height}
    />
  );
}  